import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const authHeaders = (authToken) => ({
  'Content-Type': 'application/json',
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
});

/** Public URL a share code opens. Always the current site, so dev links stay on dev. */
export const shareUrl = (shareId) => `${window.location.origin}/r/${shareId}`;

/**
 * The owner's current share for a report.
 * @returns {Promise<object|null>} {share_id, access, pin, expires_at, view_count, last_viewed_at} or null
 */
const getShare = async (jobId, authToken) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/jobs/${encodeURIComponent(jobId)}/share`,
    { headers: authHeaders(authToken) }
  );
  return response.data?.data?.share ?? null;
};

/**
 * Create the share, or update it in place (the link never changes).
 * Only send `expires_at` when the owner changes it: omitting it keeps the
 * current expiry (a new link defaults to 1 day), while null means "never".
 * @param {{access:'public'|'locked', expires_at?:string|null, regenerate_pin?:boolean}} settings
 */
const saveShare = async (jobId, settings, authToken) => {
  const response = await axios.put(
    `${API_BASE_URL}/api/jobs/${encodeURIComponent(jobId)}/share`,
    settings,
    { headers: authHeaders(authToken) }
  );
  return response.data?.data?.share ?? null;
};

/** Stop sharing. The link is dead for good; sharing again creates a new one. */
const revokeShare = async (jobId, authToken) => {
  await axios.delete(
    `${API_BASE_URL}/api/jobs/${encodeURIComponent(jobId)}/share`,
    { headers: authHeaders(authToken) }
  );
};

/**
 * Load a shared report as a viewer. No auth. POST so the PIN stays out of URLs.
 * Resolves {status:'ok', report} or {status:'pin_required'|'invalid_pin'|'locked_out'|'not_found'|'error', ...}.
 */
const viewSharedReport = async (shareId, pin) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/shared/${encodeURIComponent(shareId)}`,
      pin ? { pin } : {},
      { headers: { 'Content-Type': 'application/json' } }
    );
    return { status: 'ok', report: response.data?.data };
  } catch (err) {
    const code = err.response?.status;
    const data = err.response?.data?.data || {};
    if (code === 401) {
      return data.invalid_pin
        ? { status: 'invalid_pin', attemptsLeft: data.attempts_left }
        : { status: 'pin_required' };
    }
    if (code === 429) return { status: 'locked_out', retryAfterSeconds: data.retry_after_seconds };
    if (code === 404) return { status: 'not_found' };
    return { status: 'error' };
  }
};

const shareService = {
  getShare,
  saveShare,
  revokeShare,
  viewSharedReport,
};

export default shareService;
